import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../../lib/api';
import { adaptCampaignToUI, adaptRecipientToUI, UICampaignRecipient } from '../../../../utils/campaignAdapter';
import { CampaignData, ChatMessage, FilterStatus } from '../types';
import { generateCampaignSummary, typeMessage } from '../utils/campaignHelpers';
import { EmailTemplate } from './useCampaignState';

const RECIPIENTS_PAGE_SIZE = 20;

/**
 * Map UI filter status to API status param.
 * Engagement-based filters (opened, clicked, replied) have no API equivalent,
 * so they return undefined (fetch all recipients).
 */
function mapFilterToAPIStatus(filter: FilterStatus): string | undefined {
  switch (filter) {
    case 'draft': return 'enrolled';
    case 'queued': return 'enrolled';
    case 'bounced': return 'bounced';
    case 'sent': return 'completed';
    default: return undefined;
  }
}

interface UseCampaignEffectsProps {
  templateId: string | undefined;
  campaign: CampaignData | null;
  isLoading: boolean;
  chatHasAnimatedIn: boolean;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  chatMessages: ChatMessage[];
  filterStatus: FilterStatus;
  searchQuery: string;
  currentPage: number;
  recipientsVersion: number;
  setIsLoading: (loading: boolean) => void;
  setCampaign: (campaign: CampaignData | null) => void;
  setEditName: (name: string) => void;
  setEditSubject: (subject: string) => void;
  setEditBody: (body: string) => void;
  setEditSendWindowStart: (start: string) => void;
  setEditSendWindowEnd: (end: string) => void;
  setEditSendDays: (days: string[]) => void;
  setIsChatOpen: (open: boolean) => void;
  setChatHasAnimatedIn: (animated: boolean) => void;
  setIsChatLoading: (loading: boolean) => void;
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setTypingText: (text: string) => void;
  setIsTypingComplete: (complete: boolean) => void;
  setRecipients: React.Dispatch<React.SetStateAction<UICampaignRecipient[]>>;
  setRecipientsLoading: (loading: boolean) => void;
  setRecipientsTotal: (total: number) => void;
  setCurrentPage: (page: number) => void;
  setEmailTemplates: React.Dispatch<React.SetStateAction<EmailTemplate[]>>;
  setEmailTemplatesLoading: (loading: boolean) => void;
}

export function useCampaignEffects({
  templateId,
  campaign,
  isLoading,
  chatHasAnimatedIn,
  chatContainerRef,
  chatMessages,
  filterStatus,
  searchQuery,
  currentPage,
  recipientsVersion,
  setIsLoading,
  setCampaign,
  setEditName,
  setEditSubject,
  setEditBody,
  setEditSendWindowStart,
  setEditSendWindowEnd,
  setEditSendDays,
  setIsChatOpen,
  setChatHasAnimatedIn,
  setIsChatLoading,
  setChatMessages,
  setTypingText,
  setIsTypingComplete,
  setRecipients,
  setRecipientsLoading,
  setRecipientsTotal,
  setCurrentPage,
  setEmailTemplates,
  setEmailTemplatesLoading,
}: UseCampaignEffectsProps) {
  const navigate = useNavigate();
  // Ref for debounce timer
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to guard chat summary — using ref instead of state to avoid re-render cancelling timers
  const hasShownSummaryRef = useRef(false);

  // Reset chat summary ref when switching campaigns
  useEffect(() => {
    hasShownSummaryRef.current = false;
  }, [templateId]);

  // Effect 1: Load campaign detail + Gmail status (no recipients)
  useEffect(() => {
    if (!templateId) return;
    setIsLoading(true);

    const loadCampaign = async () => {
      try {
        const [campaignData, gmailStatus] = await Promise.all([
          apiClient.getCampaign(templateId),
          apiClient.getGmailConnectionStatus().catch(() => ({ is_connected: false, gmail_email: null })),
        ]);

        let senderEmail = 'user@company.com';
        if (gmailStatus.is_connected && gmailStatus.gmail_email) {
          senderEmail = gmailStatus.gmail_email;
        }

        // Adapt to UI format with empty recipients array — stats come from campaign.stats
        const data = adaptCampaignToUI(campaignData, [], senderEmail);

        setCampaign(data);
        setEditName(data.name);
        setEditSubject(data.subject);
        setEditBody(data.body);
        setEditSendWindowStart(data.sendWindow.start);
        setEditSendWindowEnd(data.sendWindow.end);
        setEditSendDays(data.sendWindow.days);
      } catch (error: any) {
        console.error('Failed to load campaign:', error);
        // If campaign not found (deleted), redirect to campaigns list
        if (error?.response?.status === 404) {
          navigate('/campaigns', { replace: true });
          return;
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaign();
  }, [templateId]);

  // Effect 2: Load recipients (server-side pagination + search + filter)
  useEffect(() => {
    if (!templateId) return;

    const loadRecipients = () => {
      setRecipientsLoading(true);

      const apiStatus = mapFilterToAPIStatus(filterStatus);
      const offset = (currentPage - 1) * RECIPIENTS_PAGE_SIZE;

      apiClient
        .listCampaignRecipients(templateId, {
          status: apiStatus,
          search: searchQuery || undefined,
          limit: RECIPIENTS_PAGE_SIZE,
          offset,
        })
        .then((response) => {
          const campaignStatus = campaign?.status === 'active' ? 'active' :
            campaign?.status === 'paused' ? 'paused' : 'draft';

          const uiRecipients = (response.recipients || []).map(
            (r: any) => adaptRecipientToUI(r, campaignStatus)
          );
          setRecipients(uiRecipients);
          setRecipientsTotal(response.total || 0);
        })
        .catch((error) => {
          console.error('Failed to load recipients:', error);
          setRecipients([]);
          setRecipientsTotal(0);
        })
        .finally(() => {
          setRecipientsLoading(false);
        });
    };

    // Debounce search changes (300ms), immediate for page/filter changes
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Always debounce slightly to batch rapid changes
    searchDebounceRef.current = setTimeout(loadRecipients, searchQuery ? 300 : 0);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [templateId, filterStatus, searchQuery, currentPage, campaign?.status, recipientsVersion]);

  // Effect 3: Reset page to 1 when filter, search, or campaign changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchQuery, templateId]);

  // Effect 4: Load email templates on mount
  useEffect(() => {
    setEmailTemplatesLoading(true);
    apiClient.getEmailTemplates()
      .then((data) => {
        const templates: EmailTemplate[] = (data.templates || data || []).map((t: any) => ({
          id: t.id,
          name: t.name,
          subject: t.subject || '',
          body: t.body || '',
        }));
        setEmailTemplates(templates);
      })
      .catch((error) => {
        console.error('Failed to load email templates:', error);
        setEmailTemplates([]);
      })
      .finally(() => {
        setEmailTemplatesLoading(false);
      });
  }, []);

  // Chat scroll
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Auto-open chat
  useEffect(() => {
    if (campaign && !chatHasAnimatedIn) {
      const timer = setTimeout(() => {
        setIsChatOpen(true);
        setChatHasAnimatedIn(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [campaign, chatHasAnimatedIn]);

  // Chat summary — uses ref guard to prevent re-render from cancelling timers
  useEffect(() => {
    if (campaign && !hasShownSummaryRef.current && !isLoading) {
      hasShownSummaryRef.current = true;

      const summary = generateCampaignSummary(
        campaign.name,
        campaign.stats,
        campaign.recipients
      );

      const showLoadingTimer = setTimeout(() => {
        setIsChatLoading(true);
      }, 300);

      const displaySummaryTimer = setTimeout(() => {
        setIsChatLoading(false);
        setChatMessages([{
          id: `ai-summary-${Date.now()}`,
          role: 'assistant',
          content: summary,
          timestamp: new Date(),
          isTyping: true
        }]);
        typeMessage(summary, setTypingText, setIsTypingComplete);
      }, 1100);

      return () => {
        clearTimeout(showLoadingTimer);
        clearTimeout(displaySummaryTimer);
      };
    }
  }, [campaign, isLoading]);
}
